import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import track, { SELECTED_CHART_METRIC } from '@src/utils/sensor/track';
import { selectedMetricChart } from '@store/metricsChartContainer/actions';
import { intervalSelector } from '@store/common/selectors';
import { selectedMetricSelector, metricSelector } from '@store/metricsChartContainer/selectors';
import { getSelectedChartId } from './utils';
import MetricCard from '../MetricCard';
import useSetAnnotationsExport from '../../annotations/hooks/useSetAnnotationsExport';
import useSetMetricSeriesExport from '../hooks/export/useSetMetricSeriesExport';
import useSetTimeSeriesExport from '../hooks/export/useSetTimeSeriesExport';
import { diagnosticsModeAtom, dodAtom, unitAtom } from '../atoms';
import {
  getInvertedPercentageMetricByKey,
  getMetricDisplayName,
  getMetricByUnit,
} from '@src/utils/metrics/metricHelpers';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';

function Charts({ activeTool }) {
  const { flowId, frustrationSignalId } = useDrilldownMeta();
  const dispatch = useDispatch();
  const interval = useSelector(intervalSelector);
  const selectedMetric = useSelector(selectedMetricSelector);
  const metrics = useSelector(metricSelector);
  const unit = useAtomValue(unitAtom);
  const dodEnabled = useAtomValue(dodAtom);
  const diagMode = useAtomValue(diagnosticsModeAtom);

  // export
  useSetTimeSeriesExport();
  useSetMetricSeriesExport();
  useSetAnnotationsExport({ interval });
  // useSetAIAlertEventEport();
  // useSetManualAlertEventEport();

  // popup action moved from existing component
  const onSelect = (metricName, append = true) => {
    const chartId = getSelectedChartId(metricName);
    if (chartId) {
      const newMetricName = getInvertedPercentageMetricByKey({
        metricKey: metricName,
        defaultValue: metricName,
      });
      // Prefer the inverted (count) key when it has a card; otherwise keep the
      // clicked key. Selecting a metric with no card hides every chart in
      // diagnostics mode (only the selected card is visible).
      const metricToSelect = metrics.includes(newMetricName) ? newMetricName : metricName;
      // chart interactions should not trigger a state change
      if (selectedMetric === metricToSelect) {
        return;
      }
      dispatch(selectedMetricChart(metricToSelect, chartId, append));
      const displayName =
        getMetricDisplayName({
          metricKey: metricName,
          flowOverride: !!flowId,
          frustrationSignalOverride: !!frustrationSignalId,
        }) || {};
      track({
        event: SELECTED_CHART_METRIC,
        data: { metric: metricName, metricDisplayName: displayName, flowId },
      });
    }
  };

  return (
    <SortableContext items={metrics} strategy={rectSortingStrategy}>
      {metrics.map((metric: any) => {
        const metricKey = getMetricByUnit(unit === '%' ? '%' : '#', metric);

        return (
          <MetricCard
            key={metric}
            metric={metric}
            chartMetric={metricKey} // toggle state metric
            isSelected={selectedMetric === metric}
            onSelect={onSelect}
            diagMode={diagMode}
            showTrend={dodEnabled}
            activeTool={activeTool}
          />
        );
      })}
    </SortableContext>
  );
}

export default Charts;
